import React, { memo, useCallback, useMemo, isValidElement } from 'react';
import { Box, Theme, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import AppChip from '../../components/varietyPlanner/AppChip';
import { AnswerOptionType, BaseQuestion, FormState } from './types';
import { TranslationKeys } from '../../../i18n/locales/defaultResources';
import { CropConnectLanguages } from '../../../services/cropConnectApiClient';
import { useI18n } from '../../../hooks/useI18n';

const makeStyles = (theme: Theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    mb: 1,
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  filterLabel: {
    marginRight: 1,
    marginTop: 1,
  },
  actionGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  icon: {
    color: theme.palette.questionsForm.formBlue,
    '&:hover': {
      color: theme.palette.questionsForm.formHoverState,
    },
  },
  emptyState: { marginTop: 1 },
});

const FilterChip = memo(
  ({
    question,
    optionId,
    filterKey,
    onDelete,
  }: {
    question: BaseQuestion;
    optionId: string;
    filterKey: string;
    onDelete: (key: string, value: string) => void;
  }) => {
    //Forced to DE lng, not edtiable in translation modal as per business requirement on this component

    const { t } = useTranslation();

    const handleDelete = useCallback(() => {
      onDelete(filterKey, optionId);
    }, [filterKey, optionId, onDelete]);

    const abbreviationKey =
      `varietyPlanner_${question.tag}_Abbreviation` as keyof typeof TranslationKeys;

    const option = question.options?.find((opt) => opt.id === optionId);

    let optionKey: keyof typeof TranslationKeys;
    if (option?.type === AnswerOptionType.Multiple) {
      optionKey =
        `varietyPlanner_${question.tag}_${option.tag?.toUpperCase()}` as keyof typeof TranslationKeys;
    } else {
      optionKey =
        `varietyPlanner_${question.tag}_${option?.type?.toUpperCase()}_TAG` as keyof typeof TranslationKeys;
    }

    const abbreviationText = t(abbreviationKey as TranslationKeys, {
      lng: CropConnectLanguages.DeDE,
    });
    const optionText = t(optionKey as TranslationKeys, {
      lng: CropConnectLanguages.DeDE,
    });

    const labelContent = useMemo(() => {
      if (isValidElement(abbreviationText) && isValidElement(optionText)) {
        return (
          <>
            {abbreviationText}: {optionText}
          </>
        );
      }

      return `${String(abbreviationText)}: ${String(optionText)}`;
    }, [abbreviationText, optionText]);

    return <AppChip label={labelContent} onDelete={handleDelete} />;
  }
);

interface VpFiltersProps {
  formState: FormState;
  updateFormState: (key: string, value: string | string[]) => void;
  questions?: BaseQuestion[];
}

const VpFilters: React.FC<Readonly<VpFiltersProps>> = memo(
  ({ formState, updateFormState, questions = [] }) => {
    const theme = useTheme();
    const styles = makeStyles(theme);
    const { t } = useI18n();

    const filters = useMemo(() => {
      const activeFilters: Array<{
        key: string;
        optionId: string;
        question: BaseQuestion;
      }> = [];

      Object.entries(formState).forEach(([questionId, values]) => {
        const question = questions.find((q) => q.id === questionId);
        if (!question || !values) return;

        const valueArray = Array.isArray(values) ? values : [values];

        valueArray.forEach((value) => {
          if (value && value.trim() !== '') {
            activeFilters.push({
              key: questionId,
              optionId: value,
              question,
            });
          }
        });
      });

      return activeFilters;
    }, [formState, questions]);

    const handleRemoveFilter = useCallback(
      (key: string, value: string) => {
        const currentValues = formState[key];

        if (Array.isArray(currentValues)) {
          const newValues = currentValues.filter((v) => v !== value);
          updateFormState(key, newValues);
        } else {
          updateFormState(key, '');
        }
      },
      [formState, updateFormState]
    );

    if (!filters.length) {
      return (
        <Typography
          component="p"
          variant="appBodyLarge"
          color="questionsForm.formGrey"
          sx={styles.emptyState}
        >
          {t(TranslationKeys.variety_planner_no_filters_text)}
        </Typography>
      );
    }

    return (
      <Box sx={styles.container}>
        <Box sx={styles.filterGroup}>
          <Typography
            component="p"
            variant="appBodyLarge"
            color="questionsForm.formGrey"
            sx={styles.filterLabel}
          >
            {t(TranslationKeys.variety_planner_filters_applied_text)}
          </Typography>

          {filters.map((filter) => (
            <FilterChip
              key={`${filter.key}-${filter.optionId}`}
              filterKey={filter.key}
              optionId={filter.optionId}
              question={filter.question}
              onDelete={handleRemoveFilter}
            />
          ))}
        </Box>

        {/* HIDDEN EXPORT BUTTON UNTIL V2 */}
        {/* <AppDropdown buttonText="Export" menuOptions={exportOptions} /> */}
      </Box>
    );
  }
);

export default VpFilters;
